import { all, call, put, takeLatest, takeEvery, select } from 'redux-saga/effects';

import { 
	LOAD_MEMBER,
	LOAD_MEMBER_LIST,
	LOAD_MEMBER_FOR_CONGRESS,
	LOAD_PARTY_VOTE_STATS,
	renderMember,
	renderLoadedMember,
	renderMemberList,
	renderOldMemberModal,
	updateMemberStories,
	updatePartyVoteStats
} from '../actions';
import {
	MEMBER_ENDPOINT,
	MEMBERS_ENDPOINT,
	MEMBER_STATS_ENDPOINT,
	PARTY_STATS_ENDPOINT,
	POLITICAL_POSITIONS_ENDPOINT,
	RECENT_STORIES_ENDPOINT
} from '../common/Utils';
import { getPartyStats, memberIsInCongress, memberIsLoaded, membersAreLoaded } from '../reducers';

function getStartAndEndDate() {
	const currentYear = new Date().getFullYear();
	const startDate = currentYear - 4;

	return {startDate: startDate, endDate: currentYear};
}

export function* fetchPartyVoteStats() {
	const partyStats = yield select(getPartyStats);

	if (partyStats) {
		return;
	}
	
	try {
		const {startDate, endDate} = getStartAndEndDate();
		const hdEndpoint = PARTY_STATS_ENDPOINT.concat("H/D/",startDate,"/",endDate);
		const sdEndpoint = PARTY_STATS_ENDPOINT.concat("S/D/",startDate,"/",endDate);
		const hrEndpoint = PARTY_STATS_ENDPOINT.concat("H/R/",startDate,"/",endDate);
		const srEndpoint = PARTY_STATS_ENDPOINT.concat("S/R/",startDate,"/",endDate);

		const [hd_response, sd_response, hr_response, sr_response] = 
				yield all([
							call(fetch, hdEndpoint, {mode: 'cors'}),
							call(fetch, sdEndpoint, {mode: 'cors'}),
							call(fetch, hrEndpoint, {mode: 'cors'}),
							call(fetch, srEndpoint, {mode: 'cors'})  
						]);
		const hd = yield hd_response.json();
		const sd = yield sd_response.json();
		const hr = yield hr_response.json();
		const sr = yield sr_response.json();
		const partyVoteStats = {
			house: {
				democratic: hd, 
				republican: hr
			},
			senate: {
				democratic: sd,
				republican: sr
			}
		}
		yield put(updatePartyVoteStats(partyVoteStats));
	} catch (error) {
		console.log("Error Loading Party Vote Stats");
		console.log(error);
	}
}

export function* fetchMemberList({congressId}) {
	try {
		const membersLoaded = yield select(membersAreLoaded);
		if (membersLoaded) {
			console.log("Member List is Loaded");
			return;
		}
		const endpoint = MEMBERS_ENDPOINT.concat(congressId);
		const response = yield call(fetch, endpoint, {mode: 'cors'});
		const data = yield response.json();
		let members = [];
		yield data.forEach(member => {
			members[member.personId] = member;
		});
		yield put(renderMemberList(members));
	} catch (error) {
		console.log("Error Loading Member List");
		console.log(error);
	}
}

function* fetchRecentStories(member) {
	const recentStories_ep = 
		RECENT_STORIES_ENDPOINT.concat("?per_page=6&context=embed&search=",member.lastName);
	try {
		const recentStories_response = yield call(fetch, recentStories_ep, {mode: 'cors'});
		const recentStories = yield recentStories_response.json();
		yield put(updateMemberStories(member.personId, recentStories));
	} catch (error) {
		console.log("Error Recent Stories");
		console.log(error);
	}	
}

function* fetchMemberStats(personId) {
	const {startDate, endDate} = getStartAndEndDate();
	const member_stats_ep = MEMBER_STATS_ENDPOINT.concat(personId,"/",startDate,"/",endDate);
	try {
		const member_stats_response = yield call(fetch, member_stats_ep, {mode: 'cors'});
		const memberStats = yield member_stats_response.json();
		return memberStats;
	} catch (error) {
		console.log("Error Member Stats");
		console.log(error);
	}
}

function* fetchPoliticalPositions(personId) {
	const political_positions_ep = POLITICAL_POSITIONS_ENDPOINT.concat(personId);
	try {
		const politcal_positions_response = yield call(fetch, political_positions_ep, {mode: 'cors'});
		const politicalPositions = yield politcal_positions_response.json();
		return politicalPositions;
	} catch (error) {
		console.log("Error Loading Political Positions");
		console.log(error);
	}
}

export function* fetchMember({personId}) {
	try {
		const memberLoaded = yield select(memberIsLoaded, personId);
		if (memberLoaded) {
			console.log("Member Is Already Loaded!");
			yield put(renderLoadedMember(personId));
			return;
		}

		const person_ep = MEMBER_ENDPOINT.concat(personId);
		const bio_ep = person_ep.concat('/bio-details');
		const assignments_ep = person_ep.concat('/assignments');

		const [person_response, bio_response, assignments_response] = 
			yield all([
  						call(fetch, person_ep, {mode: 'cors'}),
						call(fetch, bio_ep, {mode: 'cors'}),
						call(fetch, assignments_ep, {mode: 'cors'}),						  
					]);

		const person = yield person_response.json();
		if ('currentMember' in person && person.currentMember === false) {
			yield put(renderMember(person));
		} else {
			const bio = yield bio_response.json();
			const assignments = yield assignments_response.json();
			const committeeAssignments = assignments.filter(assignment => "Caucus" !== assignment.committee.committeeType.label);
			const caucusAssignments = assignments.filter(assignment => "Caucus" === assignment.committee.committeeType.label);
			let member = {
				baseLoaded: true,
				...bio,
				...person,
				committeeAssignments: committeeAssignments,
				caucusAssignments: caucusAssignments
			};
			yield put(renderMember({...member}));

			const memberStats = yield fetchMemberStats(personId);
			const politicalPositions = yield fetchPoliticalPositions(personId);
			member["memberStats"] = memberStats;
			member["politicalPositions"] = politicalPositions;
			yield put(renderMember({...member}));

			yield fetchRecentStories(member);
		}
	} catch (error) {
		console.log("Error Loading Member");
		console.log(error);
		yield put(renderMember({}));
	}
}

export function* fetchMemberForCongress(props) {
	const {personId, congressId} = props;

	try {
		yield fetchMemberList({congressId});
		const validMember = yield select(memberIsInCongress, personId);
		if (validMember) {
			yield fetchMember({personId});
		} else {
			yield put(renderMember({personId: personId, currentMember: false}));
		}
	} catch (error) {
		console.log("Error Loading Member For Congrress");
		console.log(error);
		yield put(renderMember({}));
	}
}

export function* loadMemberList() {
	yield takeLatest(LOAD_MEMBER_LIST, fetchMemberList);
}

export function* loadMember() {
	yield takeEvery(LOAD_MEMBER, fetchMember);
}

export function* loadMemberForCongress() {
	yield takeEvery(LOAD_MEMBER_FOR_CONGRESS, fetchMemberForCongress);
}

export function* loadPartyVoteStats() {
	yield takeLatest(LOAD_PARTY_VOTE_STATS, fetchPartyVoteStats);
}

export default function* rootSaga() {
	yield all([loadMemberList(), loadPartyVoteStats(), loadMember(), loadMemberForCongress()]);
}